import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useTaskStore, useTaskStoreBase, type Task } from '../stores/taskStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, CustomSelect, RowActions, RowAction, StatusDot, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { priorityTone, toneOf } from '../utils/statusTone';
import { formatTableDate, useUrlPage, useSyncUrlPage, useUrlSearch } from '@redpanda/utils';
import { canModify, canAdminDelete } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { useListSearch } from '../hooks/useListSearch';
import { pendingRowClass } from '../utils/pendingItem';
import TaskDialog from '../components/TaskDialog';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { updateTaskStatus, bulkDeleteTasks, getTasks } from '../services/api';
import { toast } from 'react-hot-toast';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { TableSkeleton } from '@redpanda/ui';
import { getCustomerStatus, getArchivedRowClassName } from '../utils/customerUtils';

const TaskListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const {
    tasks,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchTasks,
  } = useTaskStore();

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname: location.pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage); // 網址 ?page=、?q=：從客戶詳情返回時回到同一頁、保留搜尋
  const [searchTerm, setSearchTerm] = useUrlSearch(urlPage);
  // 搜尋由後端查全部任務；沒有搜尋時用列表 store
  const search = searchTerm.trim();
  const searchResult = useListSearch<Task>('tasks', search ? { search } : null, page => getTasks(page, { search }), initialPage);
  const listItems = searchResult.active ? searchResult.items : tasks;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useTaskStoreBase.getState(), fetchTasks, setCurrentPage);

  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // 使用客戶限制快取 hook
  const {
    canAddCustomer,
    setCanAddCustomer,
    limitBannerRefreshTrigger,
  } = useCustomerLimitCache();

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedTasks,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    handlePageChange
  } = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('tasks.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteTasks,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useTaskStoreBase.setState,
      'task',
      () => tasks,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('tasks'); // 含「即將到期」任務重新載入
    },
    userRole,
    t,
    toast,
    // 任務只有管理員能刪（utils/permissions.ts，後端同規則）
    canDeleteItem: (task: Task) => canAdminDelete(task, userRole),
    // 智能分頁配置
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages
    } : undefined,
    fetchItems: fetchTasks,
    setCurrentPage
  });

  // 使用 ref 來穩定化函數引用，避免無限循環
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchTasksRef.current(initialPage, false);
    }
  }, [isAuthenticated, currentUserId]);

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));
  useSyncUrlPage(urlPage, listPagination, changePage);

  const handleEdit = (task: any) => {
    setEditingTask(task);
    setShowEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingTask(null);
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    setUpdatingTaskId(taskId);

    // 樂觀更新本地狀態
    const originalTasks = tasks;
    useTaskStoreBase.setState({
      items: tasks.map(task => (task.id === taskId ? { ...task, status: newStatus } : task)),
      lastOptimisticUpdate: Date.now()
    });

    try {
      await updateTaskStatus(taskId, newStatus);
      toast.success(t('tasks.list.statusUpdated', { ns: 'pages' }));
      notifyDataChanged('tasks'); // 儀表板、即將到期任務、搜尋結果重新載入
    } catch (error: any) {
      toast.error(t('tasks.list.updateFailed', { ns: 'pages' }));
      // 失敗時恢復原始狀態
      useTaskStoreBase.setState({ items: originalTasks });
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    const isOverdue = date < today;
    const isToday = date.getTime() === today.getTime();
    
    return (
      <span className={`tabular-nums ${
        isOverdue ? 'text-red-600 dark:text-red-400' :
        isToday ? 'text-amber-700 dark:text-amber-400' :
        ''
      }`}>
        {formatTableDate(date, i18n.language)}
        {isOverdue && <span className="ml-1 text-[13px]">({t('tasks.status.overdue', { ns: 'pages' })})</span>}
        {isToday && <span className="ml-1 text-[13px]">({t('tasks.status.today', { ns: 'pages' })})</span>}
      </span>
    );
  };

  const getPriorityText = (priority: string) => {
    return t(`tasks.priority.${priority}`, { ns: 'pages' }) || priority;
  };

  // 如果用戶還在載入中，先不顯示任何內容
  if (isUserLoading) {
    return null; 
  }

  // 如果用戶未認證，顯示錯誤
  if (!isAuthenticated || !currentUserId) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('tasks.list.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={selectedTasks.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
              />
            )}
            <button
              onClick={() => canAddCustomer && setShowAddDialog(true)}
              disabled={!canAddCustomer}
              className={canAddCustomer ? btnPrimary : btnDisabled}
              title={canAddCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
            >
              {t('tasks.list.addButton', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 客戶數量限制資訊 */}
      <CustomerLimitBanner 
        onLimitChange={setCanAddCustomer} 
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('tasks.list.searchPlaceholder', { ns: 'pages' })} />
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={8} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
            <TableHeader>
              <TableRow>
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={listItems.length > 0}
                />
                <TableHead className="w-40">{t('common.table.columns.title')}</TableHead>
                <TableHead className="w-36">{t('common.table.columns.customer')}</TableHead>
                <TableHead className="w-32">{t('common.table.columns.dueDate')}</TableHead>
                <TableHead className="w-36">{t('common.table.columns.status')}</TableHead>
                <TableHead className="w-24">{t('common.table.columns.priority')}</TableHead>
                <TableHead className="w-28">{t('common.table.columns.createdBy')}</TableHead>
                <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listItems.length === 0 ? (
                <ListEmptyRow
                  colSpan={8}
                  filtered={!!search}
                  title={t('tasks.list.noTasks', { ns: 'pages' })}
                  hint={t('tasks.list.emptyHint', { ns: 'pages' })}
                  action={canAddCustomer && (
                    <button type="button" onClick={() => setShowAddDialog(true)} className={btnPrimary}>{t('tasks.list.addButton', { ns: 'pages' })}</button>
                  )}
                />
              ) : (
                listItems.map((task) => (
                  <TableRow key={task.id} className={`${getArchivedRowClassName(task.customer)} ${pendingRowClass(task)}`}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(task.id)}
                      onToggle={() => toggleItemSelection(task.id)}
                      canDelete={canAdminDelete(task, userRole)}
                      disabledReason={canAdminDelete(task, userRole) ? undefined : t('common.messages.unauthorized')}
                    />
                    <TableCell>
                      <div className="font-medium truncate">
                        {task.title}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="truncate">
                        {task.customer?.id ? (
                          <Link to={`/customers/${task.customer.id}`} className="hover:text-accent transition-colors">
                            {task.customer.name}
                          </Link>
                        ) : (
                          <span className="text-fg-muted">—</span>
                        )}
                        {getCustomerStatus(task.customer).isArchived && (
                          <span className="ml-1 text-[13px] text-fg-secondary">({t('common.archived')})</span>
                        )}
                      </div>
                      {task.customer?.company && (
                        <div className="text-[13px] text-fg-secondary truncate">
                          {task.customer.company}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(task.dueDate)}
                    </TableCell>
                    <TableCell>
                      <CustomSelect
                        value={task.status}
                        onChange={(value) => handleStatusChange(task.id, value)}
                        disabled={updatingTaskId === task.id}
                        options={[
                          { value: 'PENDING', label: t('tasks.status.PENDING', { ns: 'pages' }) },
                          { value: 'COMPLETED', label: t('tasks.status.COMPLETED', { ns: 'pages' }) },
                        ]}
                        className="text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <StatusDot tone={toneOf(priorityTone, task.priority)} label={getPriorityText(task.priority)} />
                    </TableCell>
                    <TableCell className="truncate">
                      {task.createdByName || <span className="text-fg-muted">—</span>}
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        {canModify(task, userRole, currentUserId) && (
                          <RowAction onClick={() => handleEdit(task)}>{t('common.actions.edit')}</RowAction>
                        )}
                        {canAdminDelete(task, userRole) && (
                          <RowAction tone="danger" onClick={() => handleDelete(task.id)}>{t('common.actions.delete')}</RowAction>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('tasks.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('tasks.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {/* 新增任務對話框 */}
      <TaskDialog
        isOpen={showAddDialog}
        onClose={handleCloseAddDialog}
      />

      {/* 編輯任務對話框 */}
      <TaskDialog
        isOpen={showEditDialog}
        onClose={handleCloseEditDialog}
        task={editingTask}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedTasks.length, entity: t('tasks.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedTasks.length, entity: t('tasks.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default TaskListPage;